import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { reduxForm, submit, change, destroy } from 'redux-form';

import * as wizardActions from '../actions/wizard';

import WizardForm from '../components/WizardForm';

import DeviceDescriptionPage from '../components/pages/DeviceDescriptionPage';
import DeviceIdPage from '../components/pages/DeviceIdPage';
import DeviceNamePage from '../components/pages/DeviceNamePage';
import DeviceAdditionalPage from '../components/pages/DeviceAdditionalPage';
import DeviceTypePage from '../components/pages/DeviceTypePage';
import DeviceSubTypePage from '../components/pages/DeviceSubTypePage';
import OrganizationPage from '../components/pages/OrganizationPage';
import SummaryPage from '../components/pages/SummaryPage';
import ReprovisionModePage from '../components/pages/ReprovisionModePage';
import * as validator from '../utils/validate';

import sendConfiguration from '../utils/sendConfiguration';
import { WIZARD_FORM_NAME as FORM_NAME } from '../constants/common';
import DeviceLocationPage from '../components/pages/DeviceLocationPage';

const wrapWizardPage = ([page, validate]) =>
  reduxForm({
    form: FORM_NAME,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    validate,
  })(page);

const wizardPages = {
  reprovisionMode: [ReprovisionModePage, validator.validateReprovisionMode],
  type: [DeviceTypePage, validator.validateType],
  subtype: [DeviceSubTypePage, validator.validateSubType],
  name: [DeviceNamePage, validator.validateName],
  code: [DeviceIdPage, validator.validateId],
  location: [DeviceLocationPage, validator.validateLocation],
  description: [DeviceDescriptionPage, validator.validateDescription],
  additional: [DeviceAdditionalPage, validator.validateAdditionalPage],
  summary: [SummaryPage, () => ({})],
};

const nextPage = (_, dispatch) => {
  dispatch(wizardActions.nextPage());
};

const wizardSubmit = (
  deviceId,
  deviceCode,
  releaseVersion,
  osVersion,
  isReprovision
) => (values, dispatch) => {
  // Eventually the provisioning / reprovisioning need to go to different end points

  sendConfiguration(
    deviceId,
    deviceCode,
    releaseVersion,
    osVersion,
    isReprovision
  )(values, dispatch);
};

class WizardContainer extends Component {
  state = {
    pages: null,
  };

  preFillForm = device => {
    const {
      id,
      deviceType,
      deviceSubType,
      deviceName,
      deviceDescription,
    } = device;
    const fieldMap = [
      ['Id', id],
      ['Type', deviceType],
      ['SubType', deviceSubType],
      ['Name', deviceName],
      ['Description', deviceDescription],
    ];

    fieldMap.forEach(([field, value]) => {
      if (value) {
        this.props.form.change(FORM_NAME, field, value); // initialize id field
      }
    });
  };

  selectPages = async device => {
    const pages = [];

    const groups = this.props.user.groups;

    if (this.props.isReprovision) {
      pages.push(wizardPages['reprovisionMode']);
    }

    // If user has more than 1 organization we need to prompt for correct
    if (Array.isArray(groups) && groups.length > 1) {
      pages.push([OrganizationPage(groups), validator.validateOrganization]);
    } else if (Array.isArray(groups) && groups.length === 1) {
      this.props.form.change(FORM_NAME, 'Organization', groups[0].Id); // set organization
      pages.push([OrganizationPage(groups), validator.validateOrganization]);
    } else {
      this.props.actions.wizardError({
        title: 'errors.user_has_no_organizations',
        hint: 'errors.contact_administrators_hint',
      });
    }

    if (!device.deviceType) {
      pages.push(wizardPages['type']);
    }

    if (device.deviceType === 'smartCabinet') {
      pages.push(wizardPages['subtype']);
    }

    pages.push(wizardPages['name']);
    pages.push(wizardPages['location']);
    pages.push(wizardPages['description']);
    pages.push(wizardPages['additional']);
    pages.push(wizardPages['summary']);

    return pages;
  };

  componentDidMount(prevProps) {
    // Keep the done screen stays up until next refresh.
    // Also if device id in path has changed reset.
    if (
      this.props.done ||
      (this.props.target !== null &&
        (!prevProps || this.props.target.id !== prevProps.target.id))
    ) {
      this.props.actions.wizardReset();
      this.props.form.destroy(FORM_NAME);
    }

    this.preFillForm(this.props.target);
    this.selectPages(this.props.target).then(pages => {
      this.props.actions.setPageCount(pages.length);
      this.setState({ pages: pages.map(wrapWizardPage) });
    });
  }

  componentDidUpdate() {
    // Destroy inputted data after it has been sent to clear the app state.
    if (this.props.done) {
      this.props.form.destroy(FORM_NAME);
    }
  }

  render() {
    const {
      actions,
      alert,
      isLoading,
      page,
      pageCount,
      target,
      releaseVersion,
      isReprovision,
      form,
    } = this.props;

    const { pages } = this.state;

    if (!pages) {
      return null;
    }

    const currentPage = page <= pages.length ? page : 1;
    const current = pages[currentPage - 1];

    const onSubmit =
      page < pageCount
        ? nextPage
        : wizardSubmit(
            target.id,
            target.deviceCode,
            releaseVersion,
            target.osVersion,
            isReprovision
          );

    return (
      <WizardForm
        page={current}
        pageNumber={page}
        pageCount={pageCount}
        onPrev={actions.prevPage}
        onNext={() => form.submit(FORM_NAME)}
        onSubmit={onSubmit}
        formName={FORM_NAME}
        alert={alert}
        loading={isLoading}
      />
    );
  }
}

const mapStateToProps = ({ wizard, oidc, device, user, loadingBar }) => ({
  ...wizard,
  target: device.target ? device.devices[device.target] : null,
  ...user,
  isLoading: loadingBar.default,
  releaseVersion: device.releaseVersion,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...wizardActions }, dispatch),
  form: bindActionCreators({ submit, change, destroy }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardContainer);
